import { type FC, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { Icons } from '@/components/atoms/Icons'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select'
import { AssignmentSummaryHoverCard } from '@/components/molecules/AssignmentSummaryHoverCard'
import { AdminHeader } from '@/components/organisms/AdminHeader'
import { type ConfigurationHistoryEvent } from '@/model/firestore/useConfigurationHistory'
import {
  type AvailableDate,
  type AvailableRegion,
  formatDuration,
  type TypeStats,
} from '@/pages/stats/useConfigurationHistoryPage'
import { type ConfigurationHistoryStats } from '@/pages/stats/useConfigurationHistoryStats'
import { type Assignment } from '@/state/assignments/types'

export type StatsSection = 'requests' | 'configuration'

export interface ConfigurationHistoryViewProps {
  availableRegions: AvailableRegion[]
  availableDates: AvailableDate[]
  selectedRegionId: string | undefined
  selectedOperatingDate: string | undefined
  stats: ConfigurationHistoryStats
  eveningStartTime: Date | undefined
  eveningEndTime: Date | undefined
  events: ConfigurationHistoryEvent[]
  isLoading: boolean
  closestAssignments: Map<number, Assignment | null>
  isLoadingAssignments: boolean
  selectedSection: StatsSection
  onSectionChange: (section: StatsSection) => void
  onRegionChange: (regionId: string | undefined) => void
  onDateChange: (operatingDate: string | undefined) => void
  onBackToPiloteClicked: () => void
}

// Colors for timeline segments
const TOGGLE_COLORS: Record<string, Record<string, string>> = {
  assignment: {
    manual: '#94a3b8', // slate-400
    trf: '#3b82f6', // blue-500
    solver: '#22c55e', // green-500
    legacy: '#f59e0b', // amber-500
  },
  mobileRequests: {
    true: '#22c55e', // green-500
    false: '#ef4444', // red-500 (explicitly disabled by evening)
    false_central: '#94a3b8', // slate-400 (disabled by central or unknown source)
  },
  waitTime: {
    default: '#94a3b8', // slate-400
    increased: '#f59e0b', // amber-500
  },
  assistedAssignment: {
    true: '#22c55e', // green-500
    false: '#94a3b8', // slate-400
  },
  combinedAssignment: {
    disabled: '#94a3b8', // slate-400 - assisted assignment off
    manual: '#64748b', // slate-500 - manual mode
    trf: '#3b82f6', // blue-500
    solver: '#22c55e', // green-500
    legacy: '#f59e0b', // amber-500
  },
}

const TimelineRow: FC<{
  label: string
  typeStats: TypeStats | null
  operatingDate: string
  type: 'assignment' | 'mobileRequests' | 'waitTime' | 'assistedAssignment' | 'combinedAssignment'
}> = ({ label, typeStats, operatingDate, type }) => {
  const intl = useIntl()

  if (!typeStats || typeStats.segments.length === 0) {
    // Show single segment with initial value (no changes)
    return (
      <div className="mb-4">
        <div className="mb-1 text-sm font-medium text-gray-700">{label}</div>
        <div className="flex h-8 items-center rounded bg-gray-100">
          <span className="px-3 text-sm text-gray-500">
            <FormattedMessage id="configurationHistory.noChanges" />
          </span>
        </div>
      </div>
    )
  }

  // Positions are calculated per-segment within the fixed 17:00-04:00 timeline

  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-xs text-gray-500">
          {typeStats.switchCount}{' '}
          {intl.formatMessage({ id: 'configurationHistory.switches' }, { count: typeStats.switchCount })}
        </span>
      </div>
      <div className="relative h-8 overflow-hidden rounded bg-gray-100">
        {typeStats.segments.map((segment, index) => {
          const segmentStartPosition = getTimelinePosition(segment.start, operatingDate)
          const segmentEndPosition = getTimelinePosition(segment.end, operatingDate)
          const leftPercent = segmentStartPosition
          const widthPercent = segmentEndPosition - segmentStartPosition
          // For mobileRequests disabled by central (or unknown source), use gray color
          // Only use red when explicitly disabled by evening
          const colorKey =
            type === 'mobileRequests' && segment.value === false && segment.source !== 'evening'
              ? 'false_central'
              : String(segment.value)
          const color = TOGGLE_COLORS[type]?.[colorKey] ?? '#94a3b8'
          const valueLabel = getValueLabel(type, segment.value, intl)

          return (
            <div
              key={index}
              className="absolute top-0 flex h-full items-center justify-center overflow-hidden"
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                backgroundColor: color,
                minWidth: widthPercent > 0.5 ? 'auto' : '2px',
              }}
              title={`${valueLabel}: ${formatDuration(segment.durationMs)}`}>
              {widthPercent > 5 && <span className="truncate px-1 text-xs text-white">{valueLabel}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Fixed timeline range: 17:00 to 04:00 (next day)
const TIMELINE_START_HOUR = 17
const TIMELINE_END_HOUR = 4 // next day

/**
 * Calculates the position (0-100%) of a time within the fixed 17:00-04:00 timeline.
 */
function getTimelinePosition(time: Date, referenceDate: string): number {
  const timelineStart = dayjs(referenceDate).hour(TIMELINE_START_HOUR).minute(0).second(0)
  const timelineEnd = dayjs(referenceDate).add(1, 'day').hour(TIMELINE_END_HOUR).minute(0).second(0)
  const timelineDurationMs = timelineEnd.diff(timelineStart)

  const timeMs = dayjs(time).diff(timelineStart)
  return Math.max(0, Math.min(100, (timeMs / timelineDurationMs) * 100))
}

function getValueLabel(type: string, value: string | boolean, intl: ReturnType<typeof useIntl>): string {
  if (type === 'mobileRequests' || type === 'assistedAssignment') {
    return intl.formatMessage({
      id:
        value === true || value === 'true'
          ? 'configurationHistory.value.enabled'
          : 'configurationHistory.value.disabled',
    })
  }
  // Use explicit message IDs based on type and value
  const messageIdMap: Record<string, Record<string, string>> = {
    assignment: {
      manual: 'configurationHistory.value.assignment.manual',
      trf: 'configurationHistory.value.assignment.trf',
      solver: 'configurationHistory.value.assignment.solver',
      legacy: 'configurationHistory.value.assignment.legacy',
    },
    waitTime: {
      default: 'configurationHistory.value.waitTime.default',
      increased: 'configurationHistory.value.waitTime.increased',
    },
    combinedAssignment: {
      disabled: 'configurationHistory.value.combinedAssignment.disabled',
      manual: 'configurationHistory.value.assignment.manual',
      trf: 'configurationHistory.value.assignment.trf',
      solver: 'configurationHistory.value.assignment.solver',
      legacy: 'configurationHistory.value.assignment.legacy',
    },
  }
  const messageId = messageIdMap[type]?.[String(value)] ?? `configurationHistory.value.${type}.${value}`
  return intl.formatMessage({ id: messageId as Parameters<typeof intl.formatMessage>[0]['id'] })
}

const StatsSection: FC<{
  stats: ConfigurationHistoryStats
}> = ({ stats }) => {
  const intl = useIntl()

  const renderTypeStats = (
    type: 'assignment' | 'mobileRequests' | 'waitTime' | 'assistedAssignment' | 'combinedAssignment',
    label: string
  ) => {
    const typeStats = stats[type]
    if (!typeStats) {
      return (
        <div className="mb-3">
          <div className="text-sm font-medium text-gray-700">{label}</div>
          <div className="text-sm text-gray-500">
            <FormattedMessage id="configurationHistory.noData" />
          </div>
        </div>
      )
    }

    return (
      <div className="mb-3">
        <div className="mb-1 text-sm font-medium text-gray-700">{label}</div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(typeStats.valueBreakdown).map(([value, breakdown]) => {
            const valueLabel = getValueLabel(type, value === 'true' ? true : value === 'false' ? false : value, intl)
            const color = TOGGLE_COLORS[type]?.[value] ?? '#94a3b8'
            return (
              <div key={value} className="flex items-center gap-2">
                <div className="size-3 rounded" style={{ backgroundColor: color }} />
                <span className="text-sm text-gray-600">
                  {valueLabel}: {Math.round(breakdown.percentage)}% ({formatDuration(breakdown.durationMs)})
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="configurationHistory.stats.title" />
      </h3>
      <div className="mb-2 text-sm text-gray-600">
        <FormattedMessage id="configurationHistory.stats.totalSwitches" values={{ count: stats.totalSwitches }} />
      </div>
      {renderTypeStats(
        'combinedAssignment',
        intl.formatMessage({ id: 'configurationHistory.types.combinedAssignment' })
      )}
      {renderTypeStats('mobileRequests', intl.formatMessage({ id: 'configurationHistory.types.mobileRequests' }))}
      {renderTypeStats('waitTime', intl.formatMessage({ id: 'configurationHistory.types.waitTime' }))}
    </div>
  )
}

const EventsList: FC<{
  events: ConfigurationHistoryEvent[]
  closestAssignments: Map<number, Assignment | null>
  isLoadingAssignments: boolean
}> = ({ events, closestAssignments, isLoadingAssignments }) => {
  const intl = useIntl()

  // Sort events by timestamp descending (most recent first)
  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
    [events]
  )

  if (sortedEvents.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          <FormattedMessage id="configurationHistory.events.title" />
        </h3>
        <p className="text-sm text-gray-500">
          <FormattedMessage id="configurationHistory.noEvents" />
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        <FormattedMessage id="configurationHistory.events.title" />
      </h3>
      <div className="space-y-2">
        {sortedEvents.map((event) => {
          const typeLabel = intl.formatMessage({ id: `configurationHistory.types.${event.type}` })
          const isInitialSnapshot = event.previousValue === event.newValue
          const newLabel = getValueLabel(event.type, event.newValue, intl)
          const sourceLabel =
            event.type === 'mobileRequests' && event.source
              ? intl.formatMessage({ id: `configurationHistory.source.${event.source}` })
              : null
          const assignment = closestAssignments.get(event.timestamp.getTime())

          return (
            <div
              key={event._id}
              className="-mx-2 flex items-center gap-3 rounded border-b border-gray-100 px-2 py-1 last:border-b-0 hover:bg-gray-50">
              <div className="text-sm font-medium whitespace-nowrap text-gray-600">
                {dayjs(event.timestamp).format('HH:mm')}
              </div>
              <div className="flex-1 text-sm">
                <span className="font-medium text-gray-900">{typeLabel}</span>
                {sourceLabel && <span className="ml-1 text-xs text-gray-400">({sourceLabel})</span>}
                <span className="text-gray-500">
                  {isInitialSnapshot ? (
                    <>
                      : {newLabel} (
                      <FormattedMessage id="configurationHistory.events.initialSnapshot" />)
                    </>
                  ) : (
                    <>
                      : {getValueLabel(event.type, event.previousValue, intl)} → {newLabel}
                    </>
                  )}
                </span>
              </div>
              <div className="text-xs text-gray-400">{event.changedBy.username}</div>
              <div className="flex-shrink-0">
                {isLoadingAssignments ? (
                  <div className="size-4 animate-pulse rounded bg-gray-200" />
                ) : assignment ? (
                  <AssignmentSummaryHoverCard assignment={assignment}>
                    <Link
                      to="/assignments/$assignmentId"
                      params={{ assignmentId: assignment._id }}
                      target="_blank"
                      title={intl.formatMessage({ id: 'configurationHistory.events.viewAssignmentPlan' })}>
                      <Icons.shootingStar className="size-4 fill-gray-600 hover:fill-blue-500" />
                    </Link>
                  </AssignmentSummaryHoverCard>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const ConfigurationHistoryView: FC<ConfigurationHistoryViewProps> = ({
  availableRegions,
  availableDates,
  selectedRegionId,
  selectedOperatingDate,
  stats,
  eveningStartTime,
  eveningEndTime,
  events,
  isLoading,
  closestAssignments,
  isLoadingAssignments,
  selectedSection,
  onSectionChange,
  onRegionChange,
  onDateChange,
  onBackToPiloteClicked,
}) => {
  const intl = useIntl()

  return (
    <div className="flex h-full flex-col">
      <AdminHeader
        title={intl.formatMessage({ id: 'configurationHistory.title' })}
        titleClassName="text-blue-300"
        onBackToPiloteClicked={onBackToPiloteClicked}
      />

      <div className="flex-1 overflow-auto p-4 pl-16">
        {/* Section Tabs */}
        <div className="mb-6 flex gap-2">
          <button
            type="button"
            onClick={() => {
              onSectionChange('requests')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'requests' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.requests" />
          </button>
          <button
            type="button"
            onClick={() => {
              onSectionChange('configuration')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'configuration'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.configuration" />
          </button>
        </div>

        {/* Selection Controls */}
        <div className="mb-6 flex flex-wrap items-start gap-4">
          {/* Region Dropdown (mandatory) */}
          <div className="w-64">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="configurationHistory.region" />
            </label>
            <Select
              value={selectedRegionId ?? ''}
              onValueChange={(value) => {
                onRegionChange(value || undefined)
              }}>
              <SelectTrigger aria-label={intl.formatMessage({ id: 'configurationHistory.region' })}>
                <SelectValue placeholder={intl.formatMessage({ id: 'configurationHistory.selectRegion' })} />
              </SelectTrigger>
              <SelectContent>
                {availableRegions.map((region) => (
                  <SelectItem key={region.regionId} value={region.regionId}>
                    {region.regionName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Evening Dropdown (only available when region is selected) */}
          <div className="w-80">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="configurationHistory.evening" />
            </label>
            <Select
              value={selectedOperatingDate ?? ''}
              onValueChange={(value) => {
                onDateChange(value || undefined)
              }}
              disabled={!selectedRegionId || availableDates.length === 0}>
              <SelectTrigger
                aria-label={intl.formatMessage({ id: 'configurationHistory.evening' })}
                disabled={!selectedRegionId}>
                <SelectValue placeholder={intl.formatMessage({ id: 'configurationHistory.selectDate' })} />
              </SelectTrigger>
              <SelectContent>
                {availableDates.map((date) => (
                  <SelectItem key={date.date} value={date.date}>
                    {date.label}
                  </SelectItem>
                ))}
                {availableDates.length === 0 && (
                  <div className="px-4 py-2 text-gray-500">
                    <FormattedMessage id="configurationHistory.noEvening" />
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        {!selectedRegionId ? (
          <div className="flex h-96 items-center justify-center text-gray-500">
            <FormattedMessage id="configurationHistory.selectRegionPrompt" />
          </div>
        ) : !selectedOperatingDate ? (
          <div className="flex h-96 items-center justify-center text-gray-500">
            <FormattedMessage id="configurationHistory.selectDatePrompt" />
          </div>
        ) : isLoading ? (
          <div className="flex h-96 items-center justify-center text-gray-500">
            <FormattedMessage id="configurationHistory.loading" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Timeline */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                <FormattedMessage id="configurationHistory.timeline.title" />
              </h3>
              {/* Fixed time axis labels: 17:00 to 04:00 */}
              <div className="mb-2 flex justify-between text-xs text-gray-500">
                <span>17:00</span>
                <span>20:00</span>
                <span>23:00</span>
                <span>02:00</span>
                <span>04:00</span>
              </div>
              {eveningStartTime && eveningEndTime && (
                <>
                  <TimelineRow
                    label={intl.formatMessage({ id: 'configurationHistory.types.combinedAssignment' })}
                    typeStats={stats.combinedAssignment}
                    operatingDate={dayjs(eveningStartTime).format('YYYY-MM-DD')}
                    type="combinedAssignment"
                  />
                  <TimelineRow
                    label={intl.formatMessage({ id: 'configurationHistory.types.mobileRequests' })}
                    typeStats={stats.mobileRequests}
                    operatingDate={dayjs(eveningStartTime).format('YYYY-MM-DD')}
                    type="mobileRequests"
                  />
                  <TimelineRow
                    label={intl.formatMessage({ id: 'configurationHistory.types.waitTime' })}
                    typeStats={stats.waitTime}
                    operatingDate={dayjs(eveningStartTime).format('YYYY-MM-DD')}
                    type="waitTime"
                  />
                </>
              )}
            </div>

            {/* Statistics */}
            <StatsSection stats={stats} />

            {/* Events List */}
            <EventsList
              events={events}
              closestAssignments={closestAssignments}
              isLoadingAssignments={isLoadingAssignments}
            />
          </div>
        )}
      </div>
    </div>
  )
}
